import * as React from "react";
import {inject, observer} from "mobx-react";
import styled from "styled-components";
import {observable} from "mobx";
import type {IFieldPointer} from "../../../../data/models/field/Field";
import {FieldDataTypes} from "../../../../data/models/field/FieldDataTypes";
import type {IFilter} from "../../../../data/models/filter/Filter";
import type {FilterOperator} from "../../../../data/models/filter/operator/FilterOperator";
import type {AppState} from "../../../../data/state/AppState";
import {FieldDataType} from "../../../../generated/api/base";
import {ReactUtils} from "../../../utils/ReactUtils";
import LinkIcon from "../../icons/link.svg?react";
import {SelectInputV5} from "../../input/select/SelectInputV5";
import type {IFilterOperatorConfigV5} from "../operator/FilterOperatorsV5";
import {FilterOperatorsV5} from "../operator/FilterOperatorsV5";
import TrashIcon from "../../icons/trash.svg?react";
import {IconButtonStyled, IconButtonV5} from "../../interaction/IconButtonV5";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance} from "../../styles/styles";
import {AdvancedFilterFieldStyled} from "./AdvancedFilterField.styled";
import {StringParamV5} from "./controls/StringParamV5";

interface IAdvancedFilterFieldProps {
	readonly fieldRefIds: IFieldPointer[];
	readonly filter: IFilter;
	readonly onChangeField: (field: IFieldPointer) => void;
	readonly onChangeOperator: (operator: FilterOperator) => void;
	readonly onChangeParams: (params: any[]) => void;
	readonly onRemove: () => void;
	readonly operatorFilter?: (operator: FilterOperator) => boolean;
	readonly appState?: AppState;
	readonly linkedFields?: boolean;
	readonly children: React.ReactNode;
	readonly disableOperator?: boolean;
	readonly disableParam?: boolean;
	readonly operatorTooltip?: string;
	readonly onBlurParams?: () => void;
}

interface IAdvancedFilterFieldState {
	focus: boolean;
}

@inject("appState")
@observer
export class AdvancedFilterFieldV5 extends React.PureComponent<IAdvancedFilterFieldProps, IAdvancedFilterFieldState> {
	@observable
	private _appState: AppState;

	@observable
	private _linkedFields: boolean;

	constructor(props: IAdvancedFilterFieldProps) {
		super(props);
		this._appState = props.appState;
		this._linkedFields = props.linkedFields || false;

		this.state = {
			focus: false,
		};
	}

	private renderField = (fieldRefId: string) => {
		return (
			<div className="hbox alignCenter filterField">
				<span>{this._appState.actions.getFieldTitle(fieldRefId)}</span>
				{this._linkedFields && (
					<LinkIcon
						color={colorPalette.positive.c300}
						className="linkIcon"
					/>
				)}
			</div>
		);
	};

	private onFocus = (value: boolean) => {
		this.setState({focus: value});
	};

	public override componentDidUpdate(prevProps: IAdvancedFilterFieldProps) {
		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}

		if (prevProps.linkedFields !== this.props.linkedFields) {
			this._linkedFields = this.props.linkedFields;
		}
	}

	public override render() {
		const {
			onRemove,
			appState,
			filter,
			fieldRefIds,
			onChangeField,
			onChangeOperator,
			onChangeParams,
			operatorFilter,
			children,
			disableOperator,
			disableParam,
			operatorTooltip,
			onBlurParams,
		} = this.props;

		const {focus} = this.state;

		// Handle empty field (for spotlight placeholder state)
		// If field is empty, return empty (for placeholder). Otherwise, check if it's in the list or use first option
		const selectedFieldRefId = !filter.field ? "" : fieldRefIds.includes(filter.field) ? filter.field : fieldRefIds[0];
		const selectedField = selectedFieldRefId ? appState.actions.getFieldByRefId(selectedFieldRefId) : null;

		//const fields        = this.getFields();
		//const selectedField = fields.find(field => field.refId === filter.field) || fields[0];

		// Get operators only if we have a valid field
		let operators: FilterOperator[] = [];
		if (selectedField) {
			operators = FieldDataTypes.map[selectedField.dataType].operators(selectedField.dataTypeSettings);

			if (operatorFilter) {
				operators = operators.filter(operatorFilter);
			}
		}

		const operator = FilterOperatorsV5.map[filter.operator] as IFilterOperatorConfigV5;
		// Determine which param control to use: This beacuse this logic shared with spotlight filter editor
		// - If operator has a specific control, use it
		// - If operator exists but has no control, don't show param (IS_BLANK, IS_NOT_BLANK, etc.)
		// - If no operator (empty state), show StringParamV5 as placeholder
		const ParamControl = operator
			? operator.control // Use operator's control (might be null for IS_BLANK, etc.)
			: StringParamV5; // Show default text input for empty state
		const hasOperator = !!operator;

		// Check if the param control is a StringParamV5 for spotlight feature requirement
		const isStringParamControl = ParamControl === StringParamV5;

		return (
			<AdvancedFilterFieldStyled className={ReactUtils.cls("AdvancedFilterField hbox", {focus})}>
				<div className="criterias">
					<CriteriaInputsStyled>
						<div className="header">{children}</div>
						<SelectInputV5
							sort={true}
							options={fieldRefIds}
							selected={selectedFieldRefId}
							onChange={(fieldRefId) => onChangeField(fieldRefId)}
							renderOptionWhenClosed={this.renderField}
							onFocus={this.onFocus}
							stringifyOption={(fieldRefId) => appState.actions.getFieldTitle(fieldRefId)}
							placeholder="Field"
							className="SelectInput"
						/>
						<div title={disableOperator ? operatorTooltip : ""}>
							<SelectInputV5
								options={operators}
								selected={filter.operator}
								onChange={(operator) => onChangeOperator(operator)}
								className="SelectInput"
								renderOptionWhenClosed={(operation) => {
									const op = FilterOperatorsV5.map[operation];
									let label = op.label;

									if (selectedField && selectedField.dataType === FieldDataType.Boolean) {
										label = appState.actions.getBooleanFieldCustomLabelSettingsByFilterOperator(selectedField, op.id);
									}

									return label;
								}}
								placeholder="Condition"
								onFocus={this.onFocus}
								disabled={disableOperator}
							/>
						</div>
						{ParamControl ? (
							<div title={disableParam ? operatorTooltip : ""}>
								<ParamControl
									{...({
										param: filter.param,
										onChange: hasOperator ? onChangeParams : (((value: any) => onChangeParams(value)) as any),
										onFocus: this.onFocus,
										field: selectedField,
										disabled: disableParam,
										...(isStringParamControl && {onBlur: onBlurParams}),
									} as any)}
								/>
							</div>
						) : (
							<div />
						)}
						<IconButtonV5
							IconComponent={TrashIcon}
							onClick={onRemove}
							title="Delete"
							style={{minWidth: 24, height: 24}}
						/>
					</CriteriaInputsStyled>
				</div>
			</AdvancedFilterFieldStyled>
		);
	}
}

export const CriteriaInputsStyled = styled.div`
	gap: ${baseDistance.sm};
	align-items: center;
	display: grid;
	grid-template-columns: 64px 156px 128px 1fr 24px;

	${IconButtonStyled} {
		svg {
			min-width: ${baseDistance.md};
			height: ${baseDistance.md};
		}
	}
`;
